import {
  adjustToTimeZone,
  allocatableLoadInventoriesSelector,
  AllocatableLoadInventorySearchCriteria,
  AllocatableLoadInventorySnapshotEntity,
  cargoSubtype,
  ContainerDNUStatisticsSearchCriteria,
  containerDNUStatisticsSelector,
  ContainerDNUStatisticsSnapshotEntity,
  ExceedsNonPrimaryVolumeStatisticsSearchCriteria,
  exceedsNonPrimaryVolumeStatisticsSelector,
  ExceedsNonPrimaryVolumeStatisticsSnapshotEntity,
  getDefaultTimeFrame,
  StatisticsBundleWrapper,
  StatisticsSearchCriteria,
  statisticsSelector,
  timeGroup,
  TimeGroup,
  TransportCategory,
  useSearchAllocatableLoadInventoryQuery,
  useSearchContainerDNUStatisticsQuery,
  useSearchExceedsNonPrimaryVolumeStatisticsQuery,
  useSearchStatisticsQuery,
  useSearchVolumeInventoryQuery,
  volumeInventoriesSelector,
  VolumeInventorySearchCriteria,
  VolumeInventorySnapshotEntity
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo} from "react";

export type UseGetInsightsProps = {
  fromDttm: number
  toDttm?: number
  regionId?: string
  trucking?: boolean
  pipeline?: boolean
  category?: TransportCategory
}

export type UseGetInsightsResult = & {
  criteria?: StatisticsSearchCriteria
  statistics: QueryResult<StatisticsBundleWrapper | undefined>
  volumeInventories: QueryResult<VolumeInventorySnapshotEntity[]>
  allocatableLoads: QueryResult<AllocatableLoadInventorySnapshotEntity[]>
  containerDNUStatistics: QueryResult<ContainerDNUStatisticsSnapshotEntity[]>
  exceedsNonPrimaryVolumeStatistics: QueryResult<ExceedsNonPrimaryVolumeStatisticsSnapshotEntity[]>
}

export type QueryResult<T> = {
  data: T
  isLoading?: boolean
  refetch?: any
}

type SharedSearchCriteria =
  VolumeInventorySearchCriteria
  | AllocatableLoadInventorySearchCriteria
  | ContainerDNUStatisticsSearchCriteria
  | ExceedsNonPrimaryVolumeStatisticsSearchCriteria

export const useGetInsights = (props: UseGetInsightsProps): UseGetInsightsResult => {
  const {
    fromDttm,
    toDttm,
    regionId,
    category,
    trucking = true,
    pipeline = true,
  } = props

  const selectStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const selectVolumeInventories = useMemo(() => {
    return volumeInventoriesSelector()
  }, []);

  const selectAllocatableLoadInventories = useMemo(() => {
    return allocatableLoadInventoriesSelector()
  }, []);

  const selectContainerDNUStatistics = useMemo(() => {
    return containerDNUStatisticsSelector()
  }, []);

  const selectExceedsNonPrimaryVolumeStatistics = useMemo(() => {
    return exceedsNonPrimaryVolumeStatisticsSelector()
  }, []);

  const filter = useMemo(() => ({
    fromDttm: fromDttm,
    toDttm: toDttm,
  }), [fromDttm, toDttm])

  const criteria: StatisticsSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId) {
      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: adjustToTimeZone(filter.toDttm).getTime(),
        regionIds: [regionId],
        includeSubTruckCompanies: true,
        includeTruckingData: trucking,
        includePipelineData: pipeline,
        includeContainerVolumeInventoryData: true,
        categories: category ? [category.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [regionId, filter.fromDttm, filter.toDttm, trucking, pipeline, category])

  const sharedSearchCriteria: SharedSearchCriteria | any = useMemo(() => {
    if (regionId) {
      const defaultTimeFrame = getDefaultTimeFrame(filter.fromDttm, filter.toDttm)

      let criteriaTimeGroup: TimeGroup
      if (defaultTimeFrame === "MONTH" || defaultTimeFrame === "WEEK" || defaultTimeFrame === "DAY") {
        criteriaTimeGroup = timeGroup.daily
      } else {
        criteriaTimeGroup = timeGroup.hourly
      }

      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: filter.toDttm ? adjustToTimeZone(filter.toDttm).getTime() : undefined,
        timeGroup: criteriaTimeGroup.code,
        regionId: regionId,
        cargoSubtypes: [(category?.cargoSubtype ?? cargoSubtype.water).code],
      }
    } else {
      return skipToken
    }
  }, [regionId, filter.fromDttm, filter.toDttm, category?.cargoSubtype])

  const statisticsResults = useSearchStatisticsQuery(criteria, {
    pollingInterval: 3600000,
    selectFromResult: (result) => ({
      ...result,
      data: selectStatistics(result, undefined),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const volumeInventoryResults = useSearchVolumeInventoryQuery(sharedSearchCriteria, {
    selectFromResult: (result) => ({
      ...result,
      data: selectVolumeInventories(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const allocatableLoadResults = useSearchAllocatableLoadInventoryQuery(sharedSearchCriteria, {
    selectFromResult: (result) => ({
      ...result,
      data: selectAllocatableLoadInventories(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const containerDNUStatisticsResults = useSearchContainerDNUStatisticsQuery(sharedSearchCriteria, {
    selectFromResult: (result) => ({
      ...result,
      data: selectContainerDNUStatistics(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const exceedsNonPrimaryVolumeStatisticsResults = useSearchExceedsNonPrimaryVolumeStatisticsQuery(sharedSearchCriteria, {
    selectFromResult: (result) => ({
      ...result,
      data: selectExceedsNonPrimaryVolumeStatistics(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  return {
    criteria: criteria === skipToken ? undefined : criteria,
    statistics: statisticsResults,
    volumeInventories: volumeInventoryResults,
    allocatableLoads: allocatableLoadResults,
    containerDNUStatistics: containerDNUStatisticsResults,
    exceedsNonPrimaryVolumeStatistics: exceedsNonPrimaryVolumeStatisticsResults,
  }
}
